import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LABELS } from "@match/shared";
import { ApproximateNote } from "@/components/session";
import { Button, Dash, ErrorNote, Field, SidePanel, inputClass, useCurrentUser } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { date, money, signClass } from "@/lib/format";
import { keys } from "@/lib/queries";
import { decimalInput } from "@/lib/units";
import type { Account, ClientDetail, LedgerRow, TransactionKind } from "@/lib/types";

/*
 * Registro movimenti del cliente e riconciliazione dei conti.
 *
 * Le righe non si modificano: si stornano. L'importo è nella valuta del
 * movimento; accanto, il valore in reporting col tasso congelato e la sua
 * fonte, marcato come stima quando è il riferimento BCE.
 */

export const KIND_LABEL: Record<TransactionKind, string> = {
  DEPOSIT: "Versamento",
  WITHDRAWAL: "Prelievo",
  PROP_FEE: "Fee prop",
  PROP_FEE_REFUND: "Rimborso fee",
  PAYOUT_RECEIVED: "Payout ricevuto",
  MANAGER_COMMISSION_DUE: "Commissione maturata",
  MANAGER_COMMISSION_PAID: "Commissione pagata",
  ADJUSTMENT: "Rettifica",
};

/** Segno imposto dal tipo: l'operatore scrive l'importo, il segno lo mette il form. */
const SIGN: Partial<Record<TransactionKind, 1 | -1>> = {
  DEPOSIT: 1,
  WITHDRAWAL: -1,
  PROP_FEE: -1,
  PROP_FEE_REFUND: 1,
  MANAGER_COMMISSION_DUE: -1,
  MANAGER_COMMISSION_PAID: 1,
};

const ACCOUNT_KINDS: TransactionKind[] = ["DEPOSIT", "WITHDRAWAL"];
const CYCLE_KINDS: TransactionKind[] = ["PROP_FEE", "PROP_FEE_REFUND", "MANAGER_COMMISSION_DUE", "MANAGER_COMMISSION_PAID"];
/** Il payout ricevuto nasce dal payout segnato come pagato, non dal form. */
const FORM_KINDS = (Object.keys(KIND_LABEL) as TransactionKind[]).filter((k) => k !== "PAYOUT_RECEIVED");

const FX_SOURCE_LABEL = { IDENTITY: "stessa valuta", MANUAL: "a mano", BROKER_FEED: "feed broker", ECB: "BCE" } as const;

/** Chi scrive nel registro: come il backend, anche CONTABILE. */
export function useCanWriteLedger(): boolean {
  const role = useCurrentUser().role;
  return role === "ADMIN" || role === "OPERATORE" || role === "CONTABILE";
}

export function ClientLedger({ client }: { client: ClientDetail }) {
  const canWrite = useCanWriteLedger();
  const [kind, setKind] = useState<TransactionKind | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [panel, setPanel] = useState(false);

  const q = { kind: kind || undefined, from: from || undefined, to: to ? `${to}T23:59:59.999Z` : undefined };
  const ledger = useQuery({ queryKey: [...keys.ledger(client.id), q], queryFn: () => api.ledger.list(client.id, q) });
  const reporting = ledger.data?.totals.reportingCurrency ?? "EUR";

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <h2 className="mr-auto text-sm font-medium text-ink">Registro movimenti</h2>
        <FilterField label="Tipo">
          {(id) => (
            <select id={id} value={kind} onChange={(e) => setKind(e.target.value as TransactionKind | "")} className={`${inputClass} w-48 py-1.5`}>
              <option value="">Tutti</option>
              {(Object.keys(KIND_LABEL) as TransactionKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          )}
        </FilterField>
        <FilterField label="Dal">{(id) => <input id={id} type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputClass} py-1.5`} />}</FilterField>
        <FilterField label="Al">{(id) => <input id={id} type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${inputClass} py-1.5`} />}</FilterField>
        {canWrite && (
          <Button variant="primary" onClick={() => setPanel(true)}>
            Nuovo movimento
          </Button>
        )}
      </div>

      {ledger.error ? (
        <ErrorNote>{errorMessage(ledger.error)}</ErrorNote>
      ) : !ledger.data ? (
        <p className="text-sm text-muted">Caricamento…</p>
      ) : ledger.data.rows.length === 0 ? (
        <p className="rounded-card border border-dashed border-line px-4 py-6 text-sm text-muted">
          {kind || from || to
            ? "Nessun movimento con questi filtri."
            : "Nessun movimento. Registra versamenti e prelievi del conto broker, le fee pagate alla prop e le commissioni: servono alla riconciliazione e ai totali del ciclo."}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs text-faint">
                  <th className="py-2 pr-4 font-medium">Data</th>
                  <th className="py-2 pr-4 font-medium">Tipo</th>
                  <th className="py-2 pr-4 font-medium">Conto o ciclo</th>
                  <th className="py-2 pr-4 text-right font-medium">Importo</th>
                  <th className="py-2 pr-4 text-right font-medium">In {reporting}</th>
                  <th className="py-2 pr-4 font-medium">Riferimento</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {ledger.data.rows.map((r) => (
                  <LedgerLine key={r.id} row={r} clientId={client.id} canWrite={canWrite} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted">
            Saldo del registro{" "}
            {Object.entries(ledger.data.totals.byCurrency).map(([cur, v], i) => (
              <span key={cur}>
                {i > 0 && " · "}
                <span className={signClass(v)}>{money(v, cur)}</span>
              </span>
            ))}
            {" · "}in {reporting} <span className={signClass(ledger.data.totals.reporting)}>{money(ledger.data.totals.reporting, reporting)}</span>
            {ledger.data.totals.fxAssumed && <ApproximateNote title="Almeno un movimento è convertito col riferimento BCE, non col cambio del conto." />}
          </p>
        </>
      )}

      <TransactionPanel open={panel} client={client} reportingCurrency={reporting} onClose={() => setPanel(false)} />
    </section>
  );
}

function FilterField({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  return (
    <div className="[&_label]:mb-1 [&_label]:text-xs">
      <Field label={label}>{children}</Field>
    </div>
  );
}

function LedgerLine({ row: r, clientId, canWrite }: { row: LedgerRow; clientId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [reversing, setReversing] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amount = Number(r.amount);
  const stornabile = canWrite && !r.reversesId && !r.reversedBy && !r.payoutId;

  const reverse = async () => {
    if (reason.trim().length < 3) return setError("Scrivi il motivo");
    setBusy(true);
    setError(null);
    try {
      await api.ledger.reverse(r.id, reason.trim());
      await Promise.all([qc.invalidateQueries({ queryKey: keys.ledger(clientId) }), qc.invalidateQueries({ queryKey: ["reconciliation"] }), qc.invalidateQueries({ queryKey: keys.client(clientId) })]);
      setReversing(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className={`border-b border-line/60 align-top ${r.reversedBy ? "text-muted" : ""}`}>
      <td className="py-2.5 pr-4 text-muted">{date(r.occurredAt)}</td>
      <td className="py-2.5 pr-4">
        {KIND_LABEL[r.kind]}
        {r.reversesId && <span className="block text-xs text-faint">storno</span>}
        {r.reversedBy && <span className="block text-xs text-faint">stornato il {date(r.reversedBy.occurredAt)}</span>}
      </td>
      <td className="py-2.5 pr-4">
        {r.account ? (
          <span className={r.account.role === "PROP" ? "text-prop" : r.account.role === "BROKER" ? "text-broker" : ""}>{r.account.label}</span>
        ) : r.cycle ? (
          <span>{r.cycle.label ?? LABELS.cyclePhase[r.cycle.currentPhase]}</span>
        ) : (
          <Dash />
        )}
      </td>
      <td className={`py-2.5 pr-4 text-right ${signClass(amount)}`}>{money(amount, r.currency)}</td>
      <td className="py-2.5 pr-4 text-right">
        <span className={signClass(Number(r.amountReporting))}>
          {r.fxAssumed ? "≈ " : ""}
          {money(r.amountReporting, r.reportingCurrency)}
        </span>
        <span className="block text-xs text-faint" title={r.fxAssumed ? "Riferimento BCE, non il cambio del conto" : undefined}>
          {r.fxSource === "IDENTITY" ? FX_SOURCE_LABEL.IDENTITY : `${Number(r.fxRate)} · ${FX_SOURCE_LABEL[r.fxSource]}`}
        </span>
      </td>
      <td className="py-2.5 pr-4 text-muted">
        {r.reference ?? <Dash />}
        {r.notes && <span className="block text-xs text-faint">{r.notes}</span>}
      </td>
      <td className="py-2.5 text-right">
        {reversing ? (
          <div className="ml-auto w-56 space-y-1.5 text-left">
            <input aria-label="Motivo dello storno" placeholder="Motivo dello storno" value={reason} onChange={(e) => setReason(e.target.value)} className={`${inputClass} py-1`} />
            {error && <p className="text-xs text-loss">{error}</p>}
            <div className="flex justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => setReversing(false)}>
                Annulla
              </Button>
              <Button size="sm" variant="danger" disabled={busy} onClick={() => void reverse()}>
                Conferma storno
              </Button>
            </div>
          </div>
        ) : (
          stornabile && (
            <Button size="sm" variant="ghost" onClick={() => setReversing(true)}>
              Storna
            </Button>
          )
        )}
      </td>
    </tr>
  );
}

function TransactionPanel({ open, client, reportingCurrency, onClose }: { open: boolean; client: ClientDetail; reportingCurrency: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<TransactionKind>("DEPOSIT");
  const [amount, setAmount] = useState("");
  const [negative, setNegative] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [cycleId, setCycleId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsAccount = ACCOUNT_KINDS.includes(kind);
  const needsCycle = CYCLE_KINDS.includes(kind);
  const account = client.accounts.find((a) => a.id === accountId);
  const effectiveCurrency = account?.currency ?? currency;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAccountId(client.accounts.find((a) => a.role === "BROKER")?.id ?? "");
    setCycleId(client.cycles[0]?.id ?? "");
  }, [open, client]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const v = decimalInput(amount.replace(/^-/, ""));
    if (!v || Number(v) === 0) return setError("Scrivi un importo diverso da zero");
    if (needsAccount && !accountId) return setError("Scegli il conto");
    if (needsCycle && !cycleId) return setError("Scegli il ciclo");
    if (kind === "ADJUSTMENT" && !notes.trim()) return setError("Una rettifica richiede il motivo");
    const rate = fxRate.trim() ? decimalInput(fxRate) : null;
    if (fxRate.trim() && !rate) return setError("Il tasso deve essere un numero");
    const sign = SIGN[kind] ?? (negative ? -1 : 1);

    setBusy(true);
    try {
      await api.ledger.create({
        clientId: client.id,
        kind,
        amount: sign < 0 ? `-${v}` : v,
        currency: needsAccount || accountId ? undefined : currency,
        accountId: needsAccount ? accountId : null,
        cycleId: needsCycle ? cycleId : null,
        occurredAt: new Date(`${occurredAt}T12:00:00Z`).toISOString(),
        reference: reference.trim() || null,
        notes: notes.trim() || null,
        ...(rate ? { fxRate: rate } : {}),
      });
      await Promise.all([qc.invalidateQueries({ queryKey: keys.ledger(client.id) }), qc.invalidateQueries({ queryKey: ["reconciliation"] }), qc.invalidateQueries({ queryKey: keys.client(client.id) })]);
      setAmount("");
      setReference("");
      setNotes("");
      setFxRate("");
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const askRate = effectiveCurrency !== reportingCurrency || /Tasso .* non disponibile/.test(error ?? "");

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Nuovo movimento"
      subtitle={client.name}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="ledger-form" disabled={busy}>
            {busy ? "Registrazione…" : "Registra"}
          </Button>
        </>
      }
    >
      <form id="ledger-form" onSubmit={submit} className="space-y-4">
        <p className="rounded border border-line bg-raised/50 p-3 text-sm text-muted">Una riga registrata non si modifica: se è sbagliata si storna e se ne registra un'altra.</p>
        <Field label="Tipo">
          {(id) => (
            <select id={id} value={kind} onChange={(e) => setKind(e.target.value as TransactionKind)} className={inputClass}>
              {FORM_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          )}
        </Field>
        {needsAccount && (
          <Field label="Conto" hint="La valuta del movimento è quella del conto.">
            {(id) => (
              <select id={id} value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputClass}>
                <option value="" disabled>
                  Scegli il conto
                </option>
                {client.accounts.map((a: Account) => (
                  <option key={a.id} value={a.id}>
                    {a.label} · {a.currency}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        {needsCycle && (
          <Field label="Ciclo">
            {(id) => (
              <select id={id} value={cycleId} onChange={(e) => setCycleId(e.target.value)} className={inputClass}>
                <option value="" disabled>
                  Scegli il ciclo
                </option>
                {client.cycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label ?? `${c.propFirm.name} ${c.preset.name}`} · {LABELS.cyclePhase[c.currentPhase]}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Importo" hint={SIGN[kind] ? (SIGN[kind]! < 0 ? "Registrato in uscita." : "Registrato in entrata.") : undefined}>
            {(id) => <input id={id} inputMode="decimal" value={amount} placeholder="0.00" onChange={(e) => setAmount(e.target.value)} className={inputClass} />}
          </Field>
          {needsAccount ? (
            <Field label="Valuta">{(id) => <input id={id} disabled value={account?.currency ?? ""} className={inputClass} />}</Field>
          ) : (
            <Field label="Valuta" hint="Quella in cui il movimento avviene o si calcola.">
              {(id) => <input id={id} value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={inputClass} />}
            </Field>
          )}
        </div>
        {!SIGN[kind] && (
          <label className="flex items-center gap-2 text-sm text-muted">
            <input type="checkbox" checked={negative} onChange={(e) => setNegative(e.target.checked)} /> Rettifica in uscita (negativa)
          </label>
        )}
        <Field label="Data">{(id) => <input id={id} type="date" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} className={inputClass} />}</Field>
        {askRate && (
          <Field label={`Tasso ${effectiveCurrency}/${reportingCurrency}`} hint="Vuoto = feed del broker, poi BCE. Si scrive se nessuno dei due è disponibile: si congela sul movimento.">
            {(id) => <input id={id} inputMode="decimal" value={fxRate} placeholder="es. 0.9200" onChange={(e) => setFxRate(e.target.value)} className={inputClass} />}
          </Field>
        )}
        <Field label="Riferimento" hint="Bonifico, hash, id del payout sulla piattaforma della prop.">
          {(id) => <input id={id} value={reference} onChange={(e) => setReference(e.target.value)} className={inputClass} />}
        </Field>
        <Field label={kind === "ADJUSTMENT" ? "Motivo" : "Note"}>
          {(id) => <textarea id={id} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />}
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}
